import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { LOGOS } from "@/data/assets";

const NAV_ITEMS = [
  { label: "Home", to: "/", testId: "nav-home" },
  {
    label: "Products",
    to: "/products",
    testId: "nav-products",
    children: [
      { label: "Combat 100% Whey", to: "/products/combat-whey", testId: "nav-product-whey" },
      { label: "Combat Preworkout", to: "/products/combat-preworkout", testId: "nav-product-preworkout" },
    ],
  },
  { label: "About MusclePharm", to: "/about", testId: "nav-about" },
  { label: "Authenticity", to: "/authenticity", testId: "nav-authenticity" },
  { label: "Contact", to: "/contact", testId: "nav-contact" },
];

function Logo({ testId = "site-logo" }) {
  return (
    <Link
      to="/"
      data-testid={testId}
      aria-label="MusclePharm India Home"
      className="flex items-center gap-3 group"
    >
      <img
        src={LOGOS.musclepharm}
        alt="MusclePharm"
        data-placeholder-label="MusclePharm Logo"
        className="h-9 w-auto object-contain"
      />
      <span className="hidden sm:inline-flex items-center pl-3 ml-1 border-l border-black/15 font-heading uppercase tracking-[0.3em] text-[10px] text-black/60 h-6">
        India · Official
      </span>
    </Link>
  );
}

export default function Navigation() {
  const [openMobile, setOpenMobile] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const location = useLocation();

  // Close mobile / dropdown on route change
  React.useEffect(() => {
    setOpenMobile(false);
    setOpenDropdown(false);
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [location.pathname]);

  const linkBase =
    "font-heading uppercase tracking-[0.14em] text-[13px] font-semibold text-black/85 hover:text-black transition-colors";
  const linkActive = "text-black border-b-2 border-[color:var(--mp-green)] pb-1";

  return (
    <>
      {/* Announcement bar (matches USA style) */}
      <div
        data-testid="site-announcement"
        className="w-full bg-black text-white text-center py-2 px-4 relative z-[60]"
      >
        <div className="mp-container flex items-center justify-center">
          <span className="font-heading uppercase tracking-[0.2em] text-[11px] text-white">
            <span style={{ color: "var(--mp-green)" }}>●</span>{" "}
            Officially In India · Coming Soon On Amazon
          </span>
        </div>
      </div>

      {/* Solid white header — matches musclepharm.com behaviour */}
      <header
        data-testid="site-header"
        className="sticky top-0 left-0 right-0 z-50 bg-white border-b border-black/10"
      >
        <div className="mp-container flex items-center justify-between h-[76px]">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV_ITEMS.map((item) =>
              item.children ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(true)}
                  onMouseLeave={() => setOpenDropdown(false)}
                >
                  <NavLink
                    to={item.to}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      `${linkBase} inline-flex items-center gap-1 ${isActive ? linkActive : ""}`
                    }
                  >
                    {item.label}
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${openDropdown ? "rotate-180" : ""}`}
                    />
                  </NavLink>
                  <div
                    className={`absolute left-0 top-full pt-3 min-w-[260px] ${
                      openDropdown ? "opacity-100 visible" : "opacity-0 invisible"
                    } transition-opacity`}
                    data-testid="nav-products-dropdown"
                  >
                    <div className="bg-white border border-black/10 shadow-lg">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={c.testId}
                          className={({ isActive }) =>
                            `block px-5 py-4 font-heading uppercase tracking-[0.12em] text-[12px] font-semibold border-b border-black/5 last:border-b-0 hover:bg-black hover:text-white transition-colors ${
                              isActive ? "text-[color:var(--mp-green-hover)]" : "text-black/85"
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
                  end={item.to === "/"}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Mobile toggle */}
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpenMobile((v) => !v)}
            className="lg:hidden text-black p-2"
            aria-label="Toggle menu"
          >
            {openMobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile drawer */}
        <div
          className={`lg:hidden overflow-hidden transition-[max-height] duration-300 ${
            openMobile ? "max-h-[520px]" : "max-h-0"
          }`}
          data-testid="nav-mobile-drawer"
        >
          <nav className="mp-container pb-6 pt-2 flex flex-col gap-1" aria-label="Mobile">
            {NAV_ITEMS.map((item) => (
              <div key={item.label}>
                <NavLink
                  to={item.to}
                  data-testid={`${item.testId}-mobile`}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `block py-3 border-b border-black/5 font-heading uppercase tracking-[0.16em] text-[13px] font-semibold ${
                      isActive ? "text-[color:var(--mp-green-hover)]" : "text-black/85"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
                {item.children && (
                  <div className="pl-4">
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={`${c.testId}-mobile`}
                        className={({ isActive }) =>
                          `block py-2.5 font-heading uppercase tracking-[0.12em] text-[12px] font-semibold ${
                            isActive ? "text-[color:var(--mp-green-hover)]" : "text-black/70"
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </header>
    </>
  );
}
